/**
 * Cycle Configuration Page
 * 
 * Wrapper page for the CycleConfigWizard component.
 * Creates a configuration session if one doesn't exist for the cycle,
 * then renders the wizard.
 * 
 * ============================================================================
 * RETURN-TO-ORIGIN (Batch 4)
 * ============================================================================
 * 
 * This page supports return-to-origin navigation. When navigated to with
 * an origin in location.state, it will return to that origin after:
 * - Configuration is locked (success)
 * - User clicks close/cancel (cancellation)
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Settings, AlertCircle, Loader2 } from 'lucide-react';
import { CycleConfigWizard } from '../../components/prioritization/cycle-config';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useCycleConfigStore } from '../../stores/prioritization/cycleConfigStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useReturnToOrigin } from '../../hooks/useReturnToOrigin';
import { BackLink } from '../../components/navigation/BackLink';

export default function CycleConfig() {
  const { cycleId } = useParams();
  const userId = useUserStore(state => state.userId);
  
  // Return-to-origin support (Batch 4)
  const defaultReturnRoute = cycleId ? `/prioritization/cycles/${cycleId}` : '/prioritization/cycles';
  const { handleReturn, handleCancel } = useReturnToOrigin({
    defaultReturnRoute,
    showSuccessToast: true,
    successMessage: 'Configuration saved successfully',
  });
  
  // The cycle is read the way the cycle page reads it: from the store, reactively,
  // so it arrives whenever the Layout's hydration lands. When it is not there yet
  // (a direct open of this URL, before the stores have loaded), the store asks the
  // database for this one cycle — the same path the setup wizard and the cycle
  // designer use. "Cycle not found" is shown only when that read comes back empty.
  const cycle = useDegapProcessStore(state =>
    cycleId ? state.cycles.find(c => c.id === cycleId) : undefined,
  );
  const fetchCycleById = useDegapProcessStore(state => state.fetchCycleById);

  // Configuration sessions live in the database (cycle_config_sessions); the store
  // is a cache the Layout fills on mount. Before deciding that this cycle has no
  // open session (and creating one), read the table — otherwise every visit that
  // beat the hydration created another session.
  const loadSessions = useCycleConfigStore(state => state.loadSessionsFromSupabase);
  const getActiveConfigSession = useCycleConfigStore(state => state.getActiveConfigSession);
  const createConfigSession = useCycleConfigStore(state => state.createConfigSession);
  
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Which cycle the session step has already run for — it runs once per cycle. */
  const sessionResolvedFor = useRef<string | null>(null);
  
  // Step 1: make sure the cycle is loaded.
  useEffect(() => {
    if (!cycleId) {
      setError('No cycle ID provided');
      setLoading(false);
      return;
    }
    if (cycle) return;

    setLoading(true);
    setError(null);
    void fetchCycleById(cycleId).then(result => {
      if (result.success) return; // the store now holds it; `cycle` re-renders us
      setError('Cycle not found');
      setLoading(false);
    });
  }, [cycleId, cycle, fetchCycleById]);
  
  // Step 2: once the cycle is here, use its open configuration session or start one.
  useEffect(() => {
    if (!cycleId || !cycle) return;
    if (sessionResolvedFor.current === cycleId) return;
    sessionResolvedFor.current = cycleId;
    setError(null);

    const resolveSession = async () => {
      let existingSession = getActiveConfigSession(cycleId);
      if (!existingSession) {
        await loadSessions();
        existingSession = getActiveConfigSession(cycleId);
      }
      if (existingSession) {
        setSessionId(existingSession.id);
        setLoading(false);
        return;
      }

      try {
        // Default scope: every section (criteria, weights, tools, value providers, participants).
        const newSession = createConfigSession(cycleId, `${cycle.name} configuration`, userId);
        setSessionId(newSession.id);
      } catch (_err) {
        setError('Failed to create configuration session');
      }
      setLoading(false);
    };
    void resolveSession();
  }, [cycleId, cycle, userId, getActiveConfigSession, loadSessions, createConfigSession]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }
  
  if (error || !cycleId) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--coral-dim)] mb-4">
            <AlertCircle className="h-8 w-8 text-[var(--coral)]" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
            {error || 'Invalid Cycle'}
          </h2>
          <p className="text-[var(--text-sec)] mb-6">
            Unable to load cycle configuration.
          </p>
          <Link
            to="/prioritization/cycles"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Cycles
          </Link>
        </div>
      </div>
    );
  }
  
  if (!sessionId) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--mustard-dim)] mb-4">
            <Settings className="h-8 w-8 text-[var(--mustard)]" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
            No Configuration Session
          </h2>
          <p className="text-[var(--text-sec)] mb-6">
            Unable to create a configuration session for this cycle.
          </p>
          <Link
            to={`/prioritization/cycles/${cycleId}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Cycle
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/*
        The information-architecture trail (Cycles › Configure) is already shown in
        the layout top bar by <DualBreadcrumb />. Here we only need the *return*
        affordance — where the user came from — which is a distinct concern. If they
        arrived via a detour (return-to-origin), BackLink returns them there;
        otherwise it falls back to the cycle they were configuring.
      */}
      <BackLink to={`/prioritization/cycles/${cycleId}`} label="Cycle" />

      {/* Wizard */}
      <CycleConfigWizard
        sessionId={sessionId}
        userId={userId}
        onClose={() => handleCancel()}
        onLocked={() => handleReturn({ success: true, actionType: 'updated' })}
      />
    </div>
  );
}
